import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavHotel from './NavHotel';
import { useLocation } from 'react-router-dom';
// import { loadStripe } from '@stripe/stripe-js';
// import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import star2 from '../assets/images/star2.svg';
import questionmark from '../assets/images/questionmark.svg';
import therapeut1 from '../assets/images/therapeut1.png';
import BookingService from '../services/booking.service';
import TherapistService from '../services/therapist.service';
import useApi from '../hooks/useApi';
import useAuth from '../hooks/useAuth';
import MenuHeader from './MenuHeader';
import Header from './Header';
import PromotionService from "../services/promotion.service";
import { useLoading } from '../context/LoadingProvider';
import { PhoneInput } from 'react-international-phone';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../context/NotificationProvider';
import ComparisonCard from './ComparisonCard';
import { usePrompt } from '../hooks/usePrompt';
import Modal from "react-modal";
import { useTranslatableData } from '../hooks/useTranslatable';
import PaymentService from '../services/payment.service';
import AuthService from '../services/auth.service';
import { IconButton } from '@mui/material';
import { bindEvent, getPusher, initPusher } from "../services/pusher.service";
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import {
  buildEcommerceObject,
  buildItemsFromSelectedServices,
  buildUserDataObject,
  calculateValueFromItems,
  computeTaxFromTtc,
} from "../utils/gtmEcommerce";
import {
  extractAllowedPaymentMethods,
  resolvePaymentTypeFromSelectedCard,
} from "../utils/gtmPaymentMethods";
import GtmPaymentMethodModal from "./GtmPaymentMethodModal";
import { buildGtmEventFields } from "../utils/gtmEventFields";
import GuestCheckoutDialog from "./GuestCheckoutDialog";
import { getUserDisplayName } from "../utils/userDisplay";

const formatChf = (value) => {
  if (value == null || value === '') {
    return value;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
};

const EDIT_STEP_PATHS = [
  '/choose-location',
  '/hotel-choose-service',
  '/hotel-choose-therapist',
  '/hotel-payment'
];

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const getSelectedServiceProduct = (service) =>
  service?.product || (service?.service && typeof service.service === 'object' ? service.service : null);

const resolveHotelId = ({ navState, sessionState, address, hotel, oldBooking, newBooking, selectedServices }) => firstPresent(
  navState?.hotelId,
  navState?.hotel_id,
  sessionState?.hotelId,
  sessionState?.hotel_id,
  hotel?.id,
  navState?.hotel?.id,
  sessionState?.hotel?.id,
  address?.hotel_id,
  address?.hotel?.id,
  newBooking?.hotel_id,
  newBooking?.hotel?.id,
  oldBooking?.hotel_id,
  oldBooking?.hotel?.id,
  ...(Array.isArray(selectedServices)
    ? selectedServices.map(service => getSelectedServiceProduct(service)?.hotel_id)
    : [])
);

// const stripePromise = loadStripe('pk_test_51IYwAqDh1V63ycuimBrVJgxaQkDKtUdxkpFmYv38yV8qmiCEeGCKTvlQi7cIAjepUBVaY7Tyx4xSAw8qjBdbB1ze00OanEXi4X'); // Remplacez par votre clé publique Stripe

function PaymentForm({ totalPriceCHF }) {
  // const stripe = useStripe();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const { auth } = useAuth();
  const loader = useLoading();
  const [phoneNumber, setPhoneNumber] = useState(auth?.user?.phone || '');
  const [showInput, setShowInput] = useState(false);
  const { t, i18n } = useTranslation('payment');
  const [isCguChecked, setIsCguChecked] = useState(false);
  const [showCguError, setShowCguError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);
  const [paymentMethodModalState, setPaymentMethodModalState] = useState({
    open: false,
    methods: [],
    context: null,
  });
  const [trackingUser, setTrackingUser] = useState(auth?.user || null);
  const [availabilityConflictOpen, setAvailabilityConflictOpen] = useState(false);
  const availabilityCheckInFlightRef = useRef(false);
  const lastAvailabilityCheckRef = useRef(0);
  const conflictHandledRef = useRef(false);
  const checkAvailabilityRef = useRef(null);
  const selectedTherapistIdsRef = useRef([]);
  const paymentStartedRef = useRef(false);
  const cguSectionRef = useRef(null);
  const comparisonCguSectionRef = useRef(null);

  const showCguValidationError = (ref) => {
    setShowCguError(true);
    setSubmitting(false);
    notify.warn(t('cgu_required'));

    requestAnimationFrame(() => {
      ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ref?.current?.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    setTrackingUser(auth?.user || null);
  }, [auth?.user]);

  const hasPrimaryAddress = (user) =>
    Boolean(
      user?.primary_address?.id
      || user?.primary_address?.street
      || user?.primary_address?.zip_code
      || user?.primaryAddress?.id
      || user?.primaryAddress?.street
      || user?.primaryAddress?.zip_code
    );

  const resolveTrackingUser = async () => {
    if (!auth?.user?.id) {
      return trackingUser || auth?.user || null;
    }

    const currentUser = trackingUser || auth?.user;
    if (hasPrimaryAddress(currentUser)) {
      return currentUser;
    }

    try {
      const response = await AuthService.me(
        { id: auth.user.id, with: "primary_address" },
        api
      );
      const nextUser = response?.user || currentUser;
      setTrackingUser(nextUser || null);
      return nextUser || currentUser || null;
    } catch (_) {
      return currentUser || null;
    }
  };

  const getTranslation = useCallback((key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  }, [t]);

  const navState = location.state || {};
  const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}');

  const isEditBookingMode =
    sessionStorage.getItem('editBookingMode') === 'true'
    || Boolean(navState.fromEditBooking);

  const oldBooking = isEditBookingMode
    ? JSON.parse(sessionStorage.getItem('oldBooking') || '{}')
    : null;
  const newBooking = isEditBookingMode
    ? JSON.parse(sessionStorage.getItem('newBooking') || '{}')
    : null;

  const address = navState.address ?? sessionState.address ?? newBooking?.address ?? oldBooking?.address;
  const hotel = navState.hotel ?? sessionState.hotel ?? newBooking?.hotel ?? oldBooking?.hotel;
  const duo = navState.duo ?? sessionState.duo;
  const selectedServices = navState.selectedServices ?? sessionState.selectedServices ?? [];
  const hotelId = resolveHotelId({ navState, sessionState, address, hotel, oldBooking, newBooking, selectedServices });
  const translatedSelectedServices = useTranslatableData(selectedServices);

  const totalPrice = navState.totalPrice
    ?? sessionState.totalPrice
    ?? (Array.isArray(selectedServices) && selectedServices.length > 0
      ? selectedServices.reduce((sum, s) => sum + (s?.product?.price || (s?.service && typeof s.service === 'object' ? s.service.price : 0) || 0), 0)
      : 0);
  const selectedTherapist = navState.selectedTherapists ?? sessionState.selectedTherapists ?? newBooking?.selectedTherapists ?? navState.therapists ?? sessionState.therapists ?? newBooking?.therapists ?? navState.therapist ?? sessionState.therapist ?? [];
  const normalizedSelectedTherapists = useMemo(() => {
    if (!selectedTherapist) return [];
    if (Array.isArray(selectedTherapist)) return selectedTherapist;
    return [selectedTherapist];
  }, [selectedTherapist]);
  const selectedTherapistIds = useMemo(() => {
    if (!normalizedSelectedTherapists.length) return [];
    return normalizedSelectedTherapists
      .map((therapist) => (typeof therapist === 'object' ? therapist.id : therapist))
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }, [normalizedSelectedTherapists]);
  const selectedProductIds = useMemo(() => {
    if (!Array.isArray(selectedServices)) return [];
    return selectedServices
      .map(service =>
        service?.product?.id ??
        service?.productId ??
        service?.product_id ??
        service?.service?.id ??
        service?.service_id ??
        service?.id
      )
      .filter(Boolean);
  }, [selectedServices]);
  const selectedCategoryIds = useMemo(() => {
    if (!Array.isArray(selectedServices)) return [];
    return selectedServices
      .map(service =>
        service?.category_id ??
        service?.category?.id ??
        service?.product?.category_id ??
        service?.product?.category?.id ??
        service?.service?.category_id ??
        service?.service?.category?.id ??
        service?.categoryId
      )
      .filter(Boolean);
  }, [selectedServices]);
  const selectedDate = navState.date
    ?? sessionState.date
    ?? navState.selectedDate
    ?? sessionState.selectedDate
    ?? newBooking?.date;
  const selectedTime = navState.time
    ?? sessionState.time
    ?? navState.selectedTime
    ?? sessionState.selectedTime
    ?? newBooking?.time;
  const phone = navState.phone ?? sessionState.phone;
  const no_preferences = navState.no_preferences ?? sessionState.no_preferences;
  const isSingleTherapistSequential =
    Array.isArray(selectedTherapist) &&
    selectedTherapist.length === 1 &&
    Array.isArray(selectedServices) &&
    selectedServices.length > 1 &&
    !duo;

  const {
    lat,
    lng,
    latitude,
    longitude,
    zip_code,
    street,
    street_number,
    city,
    state,
    state_code,
    country,
    country_code
  } = address || {};
  const latToUse = firstPresent(lat, latitude, navState.lat, navState.latitude, sessionState.lat, sessionState.latitude);
  const lngToUse = firstPresent(lng, longitude, navState.lng, navState.longitude, sessionState.lng, sessionState.longitude);

  const totalDuration = navState.totalDuration
    ?? sessionState.totalDuration
    ?? (Array.isArray(selectedServices) && selectedServices.length > 0
      ? selectedServices.reduce((sum, s) => sum + (Number(s?.product?.duration) || (Number(s?.service?.duration) || 0) || 0), 0)
      : 0);

  const [showComparison, setShowComparison] = useState(false);
  const [editGuardEnabled, setEditGuardEnabled] = useState(true);

  usePrompt(
    isEditBookingMode && editGuardEnabled,
    "Êtes-vous sûr de vouloir quitter la modification ? Vos changements ne seront pas enregistrés.",
    next => EDIT_STEP_PATHS.some(p => next.pathname.startsWith(p)) || next.pathname.startsWith('/commande')
  );

  const normalizeDate = useCallback((value) => {
    if (!value) return null;
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-CA');
  }, []);

  const openAvailabilityConflict = useCallback(() => {
    if (availabilityConflictOpen) return;
    setAvailabilityConflictOpen(true);
  }, [availabilityConflictOpen]);

  const handleAvailabilityConflictConfirm = useCallback(() => {
    setAvailabilityConflictOpen(false);
    setEditGuardEnabled(false);
    navigate('/hotel-choose-therapist');
  }, [navigate]);

  useEffect(() => {
    selectedTherapistIdsRef.current = selectedTherapistIds;
  }, [selectedTherapistIds]);

  const checkAvailability = useCallback(async () => {
    if (paymentStartedRef.current) {
      return;
    }

    if (availabilityConflictOpen || conflictHandledRef.current) {
      return;
    }

    if (!selectedDate || !selectedTime) {
      return;
    }

    if (!selectedProductIds.length || !totalDuration) {
      return;
    }

    if (lngToUse == null || latToUse == null || !zip_code) {
      return;
    }

    if (no_preferences) {
      return;
    }

    const preferredTherapistIds = selectedTherapistIds.filter((id) => id > 0);
    if (!preferredTherapistIds.length) {
      return;
    }

    if (availabilityCheckInFlightRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastAvailabilityCheckRef.current < 1500) {
      return;
    }

    lastAvailabilityCheckRef.current = now;
    availabilityCheckInFlightRef.current = true;

    try {
      const normalizedDate = normalizeDate(selectedDate);
      if (!normalizedDate) {
        return;
      }

      const payload = {
        date: normalizedDate,
        time: selectedTime,
        duration: totalDuration,
        lng: lngToUse,
        lat: latToUse,
        zip_code,
        products: selectedProductIds,
        is_hotel: true,
        hotel_id: hotelId,
        is_duo: duo,
        status: "ACTIVE",
        categories: selectedCategoryIds,
      };

      if (isEditBookingMode) {
        const storedNew = JSON.parse(sessionStorage.getItem("newBooking") || "{}");
        const storedOld = JSON.parse(sessionStorage.getItem("oldBooking") || "{}");
        const bookingId = storedNew?.id ?? storedOld?.id ?? oldBooking?.id ?? 0;
        if (bookingId) {
          payload.booking_id = bookingId;
        }
      }

      let therapistResponse = await TherapistService.store(payload, api, {
        with: "media,ratings,ratings.creator.media,categories",
      });

      if (duo && therapistResponse) {
        therapistResponse = Array.isArray(therapistResponse)
          ? (therapistResponse.flat ? therapistResponse.flat() : [].concat(...therapistResponse))
          : [].concat(...Object.values(therapistResponse));
        therapistResponse = Array.from(
          new Map(therapistResponse.map((therapist) => [therapist.id, therapist])).values()
        );
      }

      const availableIds = new Set(
        (Array.isArray(therapistResponse) ? therapistResponse : []).map((therapist) => therapist.id)
      );

      const stillAvailable = preferredTherapistIds.every((id) => availableIds.has(id));
      if (!stillAvailable) {
        conflictHandledRef.current = true;
        openAvailabilityConflict();
      }
    } catch (error) {
      console.error("Realtime availability check failed:", error);
    } finally {
      availabilityCheckInFlightRef.current = false;
    }
  }, [
    availabilityConflictOpen,
    selectedDate,
    selectedTime,
    totalDuration,
    lngToUse,
    latToUse,
    zip_code,
    selectedProductIds,
    selectedCategoryIds,
    selectedTherapistIds,
    no_preferences,
    duo,
    hotelId,
    api,
    isEditBookingMode,
    oldBooking,
    normalizeDate,
    openAvailabilityConflict,
  ]);

  useEffect(() => {
    checkAvailabilityRef.current = checkAvailability;
  }, [checkAvailability]);

  useEffect(() => {
    initPusher({ token: auth?.accessToken });

    const handler = (data) => {
      const payload =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch (_) {
                return {};
              }
            })()
          : data || {};

      const key = payload?.key;
      if (key !== "availability" && key !== "calendar") {
        return;
      }

      const eventTherapists = Array.isArray(payload?.payload?.therapist_ids)
        ? payload.payload.therapist_ids.map((id) => Number(id))
        : [];
      const selectedIds = selectedTherapistIdsRef.current.filter((id) => id > 0);

      if (eventTherapists.length && selectedIds.length) {
        const hasOverlap = eventTherapists.some((id) => selectedIds.includes(id));
        if (!hasOverlap) {
          return;
        }
      }

      checkAvailabilityRef.current?.();
    };

    const unbind = bindEvent("public-data", "realtime.data.updated", handler);

    return () => {
      unbind();
    };
  }, [auth?.accessToken]);

  useEffect(() => {
    initPusher({ token: auth?.accessToken });
    const pusher = getPusher();
    if (!pusher) return;

    const handleConnected = () => {
      checkAvailabilityRef.current?.();
    };

    pusher.connection.bind("connected", handleConnected);

    return () => {
      pusher.connection.unbind("connected", handleConnected);
    };
  }, [auth?.accessToken]);

  useEffect(() => {
    const handleOnline = () => {
      checkAvailabilityRef.current?.();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const closeCancelModal = () => {
    setShowCancelConfirm(false);
    setEditGuardEnabled(true);
  };

  useEffect(() => {
    if (isEditBookingMode && oldBooking && newBooking) {
      setShowComparison(true);
    }
  }, [isEditBookingMode, oldBooking, newBooking]);

  const normalizeTherapists = (booking) => {
    if (!booking) return booking || {};
    const fullFromNav = navState.selectedTherapists ?? sessionState.selectedTherapists;
    const selected = booking.selectedTherapists ?? fullFromNav;
    const therapists = (booking.therapists && booking.therapists.length && booking.therapists[0]?.firstname) ? booking.therapists : (Array.isArray(selected) && selected.length ? selected : booking.therapists);
    return { ...booking, therapists };
  };

  const normalizedOldBooking = normalizeTherapists(oldBooking);
  const normalizedNewBooking = normalizeTherapists(newBooking);
  const translatedOldBooking = useTranslatableData(normalizedOldBooking || {});
  const translatedNewBooking = useTranslatableData(normalizedNewBooking || {});


  const loadUrl = (paymentUrl) => {
    paymentStartedRef.current = true;
    window.location.replace(paymentUrl);
  }

  const loadLightbox = (paymentLightboxScript, paymentUrl, paymentMethodId = null) => {
    paymentStartedRef.current = true;
    loader.start();
    const script = document.createElement('script');
    script.src = paymentLightboxScript;
    script.async = true;
    script.onload = () => {
      window.LightboxCheckoutHandler.startPayment(paymentMethodId, () => { loader.stop(); loadUrl(paymentUrl) });
    };
    document.body.appendChild(script);
  }

  const pushAddPaymentInfoFromSelection = ({
    paymentType,
    value,
    coupon,
    items,
    userData,
    dedupeKey,
  }) => {
    if (!Array.isArray(items) || items.length === 0) return;

    pushDataLayerEvent(
      "add_payment_info",
      {
        user_data: userData,
        ...buildGtmEventFields({
          language: i18n.language,
          user: trackingUser || auth?.user,
          flypros: normalizedSelectedTherapists,
        }),
        ecommerce: buildEcommerceObject({
          currency: "CHF",
          value,
          coupon,
          tax: computeTaxFromTtc(value),
          paymentType,
          items,
        }),
      },
      {
        dedupeKey,
      }
    );
  };

  const handlePaymentMethodSelection = (method) => {
    const context = paymentMethodModalState.context;
    if (!context) return;
    paymentStartedRef.current = true;

    pushAddPaymentInfoFromSelection({
      paymentType: method?.label,
      ...(context.trackingContext || {}),
    });

    setPaymentMethodModalState({ open: false, methods: [], context: null });
    loadLightbox(context.paymentLightboxScript, context.paymentUrl, method?.id ?? null);
  };

  const closePaymentMethodModal = () => {
    setPaymentMethodModalState({ open: false, methods: [], context: null });
    setSubmitting(false);
  };

  const [handler, setHandler] = useState(null);

  useEffect(() => {
    if (phone) {
      setPhoneNumber(phone);
    }
  }, []);

  useEffect(() => {
    // if (!selectedServices || selectedServices.length === 0) {
    //   navigate('/choose-hotel-service');
    // }
    // if (!selectedTherapist || selectedTherapist.length === 0) {
    //   navigate('/choose-hotel-therapist');
    // }
    if (!auth || Object.keys(auth).length === 0) {
      sessionStorage.setItem('redirectAfterLogin', '/hotel-payment');
      const paymentState = {
        address,
        hotel,
        hotelId,
        hotel_id: hotelId,
        duo,
        phone,
        totalDuration,
        totalPrice,
        selectedServices,
        selectedTherapist,
        selectedDate,
        selectedTime
      };
      const prev = JSON.parse(sessionStorage.getItem('newBooking') || '{}');
      sessionStorage.setItem('state', JSON.stringify({ ...prev, ...paymentState }));
      // navigate('/login');
      return;
    }
  }, [selectedServices, selectedTherapist, address, hotel, hotelId, navigate]);

  // state for authenticated users
  useEffect(() => {
    if (auth && auth.user && selectedServices && selectedServices.length > 0) {
      const paymentState = {
        address,
        hotel,
        hotelId,
        hotel_id: hotelId,
        duo,
        phone,
        totalDuration,
        totalPrice,
        selectedServices,
        selectedTherapist,
        selectedDate,
        selectedTime
      };
      const prev = JSON.parse(sessionStorage.getItem('newBooking') || '{}');
      sessionStorage.setItem('state', JSON.stringify({ ...prev, ...paymentState }));
    }
  }, [selectedServices, selectedTherapist, auth, address, hotel, hotelId, duo, phone, totalDuration, totalPrice, selectedDate, selectedTime]);


  const [message, setMessage] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(totalPrice);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertMessageTel, setAlertMessageTel] = useState('');

  const isValidPhoneNumber = (number) => {
    const regex = /^[+]?[0-9]{1,4}[-\.\s]?[0-9]{1,4}[-\.\s]?[0-9]{1,4}$/;
    return regex.test(number);
  };

  const handleTelChange = (e) => {
    setPhoneNumber(e);
  };

  const handleSave = () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      setAlertMessageTel("Invalid phone number format");
      const timer = setTimeout(() => setAlertMessageTel(""), 3000);
      return () => clearTimeout(timer);
    }
    setShowInput(false);
    setPhoneNumber(phoneNumber);
    setAlertMessageTel("Phone number saved successfully!");
    setTimeout(() => {
      setAlertMessageTel("");
    }, 3000);
  };

  const fetchPromotions = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const response = await PromotionService.index(
        {
          code: promoCode,
          date_validity: `gte:${today}`,
          is_active: 1,
          with: 'usage',
        },
        api
      );
      // console.log("Promotion:", response[0]);
      if (response?.length > 0) {
        const promotion = response[0];
        if (promotion.extra?.zones_ranges?.length > 0) {
          const zipCodeInt = parseInt(zip_code, 10);
          const isZipCodeValid = promotion.extra.zones_ranges.some(zone => {
            const zoneInt = typeof zone === 'string' ? parseInt(zone, 10) : zone;
            return zoneInt === zipCodeInt;
          });
          if (isZipCodeValid) {
            console.log('Valid zip code found:', zip_code);
          } else {
            setAlertMessage(t("promo.zone_restriction"));
            return;
          }
        }
        if (promotion.all_clients) {
          // console.log("Promotion:", response[0]);
          return response[0];
        } else {
          const clientResponse = await PromotionService.index(
            {
              id: promotion.id,
              with: 'clients,usage',
            },
            api
          );
          // console.log("client Promotion:", clientResponse[0]);
          const clientHasAccess = clientResponse[0]?.clients?.some(client => client.id === auth.user.id);

          if (clientHasAccess) {
            console.log("clientHasAccess", clientHasAccess);
            return clientResponse[0];
          } else {
            setAlertMessage(t("promo.invalid"));
          }
        }
      } else {
        setAlertMessage(t("promo.invalid"));
      }
    } catch (error) {
      console.error("Error fetching promotions", error);
    }
  };

  const resetPromoToFullPrice = (message) => {
    setPromotions(null);
    setDiscountedPrice(totalPrice);
    if (message) {
      setAlertMessage(message);
    }
  };

  const getPromotionUsageLeft = (promotion) => {
    const usedPromotions = (promotion?.usage || []).filter(usage => usage.is_used);
    const userUsage = usedPromotions.filter(
      usage => String(usage.client_id) === String(auth?.user?.id)
    );
    const totalUsage = Number(promotion?.total_usage ?? -1);
    const usagePerCustomer = Number(promotion?.usage_per_customer ?? -1);
    const totalLeft = totalUsage === -1 ? Infinity : Math.max(totalUsage - usedPromotions.length, 0);
    const userLeft = usagePerCustomer === -1 ? Infinity : Math.max(usagePerCustomer - userUsage.length, 0);
    const left = Math.min(totalLeft, userLeft);

    return left === Infinity ? -1 : left;
  };

  const productMatchesPromotion = (product, promotion) => {
    if (!promotion?.product_id) return true;

    return Number(product?.id) === Number(promotion.product_id)
      || Number(product?.parent_id || 0) === Number(promotion.product_id);
  };

  const getPromotionRequiredUses = (promotion) => {
    if (!promotion?.product_id && promotion?.type !== "DURATION") {
      return 1;
    }

    return selectedServices.filter(({ product }) => {
      if (!productMatchesPromotion(product, promotion)) return false;

      if (promotion.type === "DURATION") {
        return Number(promotion.amount) === Number(product?.duration) && !product?.hotel_id;
      }

      return true;
    }).length;
  };

  const handlePromoCodeValidation = async () => {

    const promotions = await fetchPromotions();
    setDiscountedPrice(totalPrice);
    setPromotions(promotions);
    // console.log("promotions timeout:", promotions);


    const promo = promotions?.code === promoCode;
    console.log("promo:", promo);

    if (promo) {
      const currentDate = new Date();
      const validityDate = new Date(promotions.date_validity);

      if (currentDate <= validityDate) {
        let newPrice = totalPrice;
        let remainingBalance;
        if (promotions.total_usage === -1) {
          console.log("Total usage is unlimited.");
        }

        if (Array.isArray(promotions.usage) && promotions.usage.length >= 0) {
          const usedPromotions = promotions.usage.filter(usage => usage.is_used);

          if (promotions.total_usage !== -1 && usedPromotions.length >= promotions.total_usage) {
            resetPromoToFullPrice(t("promo.usage_limit_reached"));
            return;
          }

          const userUsage = promotions.usage.filter(
            usage => usage.is_used && usage.client_id === auth?.user?.id
          );

          if (promotions.usage_per_customer !== -1 && userUsage.length >= promotions.usage_per_customer) {
            resetPromoToFullPrice(t("promo.user_limit_exceeded"));
            return;
          }

          if (promotions.type === "FREE_CREDITS") {
            if (promotions.extra['remaining'] === 0) {
              setAlertMessage(t("promo.credit_expired"));
            return;
          }
            const remainingCredits = promotions.usage
              .filter(usage => usage.value > 0);
            if (remainingCredits) {
              promotions.amount = promotions.extra['remaining'];

        }
          }
        }
        console.log("selectedServices:", selectedServices);
        console.log("promo product id:", promotions.product_id);

        let updatedTotalPrice = 0;
        let hasValidProduct = false;
        let durationMismatch = false;
        let remainingUses = getPromotionUsageLeft(promotions);
        const requiredUses = getPromotionRequiredUses(promotions);

        if (requiredUses <= 0) {
          resetPromoToFullPrice(t("promo.invalid_services"));
          return;
        }

        if (remainingUses !== -1 && remainingUses < requiredUses) {
          resetPromoToFullPrice(t("promo.usage_limit_reached"));
          return;
        }

        for (const service of selectedServices) {
          const product = service.product;
          let newProductPrice = product.price;

          const isValidProduct = productMatchesPromotion(product, promotions);

          if (isValidProduct) {
            hasValidProduct = true;
            if (promotions.type === "DURATION") {
              if (product.hotel_id) {
                setAlertMessage(t("promo.invalid_hotels"));
                return;
              }

              if (promotions.amount == product?.duration && (remainingUses > 0 || remainingUses === -1)) {
                console.log("is valid duration");
                newProductPrice = 0;
                if (remainingUses !== -1) {
                  remainingUses -= 1;
                }
              } else {
                console.log("is noooo valid duration");
                durationMismatch = true;
                setAlertMessage(t("promo.invalid_duration"));
                newProductPrice = product.price;
              }
            } else if (promotions.type === "FREE") {
              newProductPrice = 0;
            } else if (promotions.type === "PERCENT_OFF") {
              newProductPrice = product.price - (product.price * promotions.amount) / 100;
            } else if (promotions.type === "FLATT_OFF" || promotions.type === "FREE_CREDITS") {
              newProductPrice = Math.max(0, product.price - promotions.amount);
            }
          }

          updatedTotalPrice += newProductPrice;
        }

        if (!hasValidProduct) {
          setAlertMessage(t("promo.invalid_services"));
          return;
        }

        console.log("Updated Total Price:", updatedTotalPrice);


        setDiscountedPrice(updatedTotalPrice);

        if (durationMismatch) {
          setAlertMessage(t("promo.invalid_duration"));
          return;
        };

        setAlertMessage(t("promo.applied_successfully"));

        if (remainingBalance >= 0) {
          console.log(`Remaining balance after discount: ${remainingBalance}`);
        }

      } else {
        setAlertMessage(t("promo.expired"));
      }
    }

    // const timer = setTimeout(() => setAlertMessage(""), 3000);
    // return () => clearTimeout(timer);

  };
  const notify = useNotifications();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    // Safety check
    if (!selectedServices || selectedServices.length === 0) {
      setSubmitting(false);
      notify.warn(t('no_services_selected'));
      return;
    }

    if (!isCguChecked) {
      showCguValidationError(cguSectionRef);
      return;
    }

    if (!hotelId) {
      setSubmitting(false);
      notify.warn(getTranslation(
        'hotel_context_missing',
        'Impossible de finaliser cette réservation hôtel. Merci de revenir à l’étape précédente et de réessayer.'
      ));
      return;
    }

    if (latToUse == null || lngToUse == null) {
      setSubmitting(false);
      notify.warn(getTranslation(
        'hotel_address_missing',
        'Impossible de finaliser cette réservation hôtel car l’adresse est incomplète. Merci de revenir à l’étape précédente et de réessayer.'
      ));
      return;
    }

    // Fix: Check both possible property names
    const therapistArray = selectedTherapist || navState.selectedTherapists || sessionState.selectedTherapists || [];

    const therapistIds = Array.isArray(therapistArray)
      ? therapistArray
        .filter(therapist => therapist && therapist.id)
        .map(therapist => therapist.id)
      : [];

    // Debug log to see what we're working with
    console.log('Therapist array:', therapistArray);
    console.log('Therapist IDs:', therapistIds);

    // Only include therapist_id if we have valid IDs
    const therapistPayload = therapistIds.length > 0 ? { therapist_id: therapistIds } : {};

    // compute final subtotal reliably using product object where available
    const finalSubtotal = promotions?.id ? discountedPrice : selectedServices
      .map(service => {
        const prod = service.product || (service.service && typeof service.service === 'object' ? service.service : null);
        return prod?.price || 0;
      })
      .reduce((acc, price) => acc + price, 0);
    const trackingItems = buildItemsFromSelectedServices(selectedServices, {
      address,
      hotelId,
    });
    const trackingValue =
      Number.isFinite(Number(finalSubtotal))
        ? Number(finalSubtotal)
        : calculateValueFromItems(trackingItems);
    const trackingCoupon =
      promotions?.id && alertMessage === t("promo.applied_successfully")
        ? promoCode
        : undefined;
    const resolvedTrackingUser = await resolveTrackingUser();
    const trackingUserData = buildUserDataObject({
      user: resolvedTrackingUser,
      address,
      phoneNumber: phoneNumber || resolvedTrackingUser?.phone || auth?.user?.phone,
      emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
      isConnectedUser: Boolean(auth?.user?.id),
    });
    const itemsSignature = trackingItems.map((item) => item.item_id).join(",");

    // Track checkout intent before triggering Wallee payment.
    try {
      if (trackingItems.length > 0) {
        pushDataLayerEvent(
          "begin_checkout",
          {
            user_data: trackingUserData,
            ...buildGtmEventFields({
              language: i18n.language,
              user: resolvedTrackingUser || auth?.user,
              flypros: normalizedSelectedTherapists,
            }),
            ecommerce: buildEcommerceObject({
              currency: "CHF",
              value: trackingValue,
              coupon: trackingCoupon,
              tax: computeTaxFromTtc(trackingValue),
              items: trackingItems,
            }),
          },
          {
            dedupeKey: createEventDedupeKey(
              "begin_checkout",
              selectedDate,
              selectedTime,
              trackingValue,
              itemsSignature
            ),
            dedupeTtlMs: 30000,
          }
        );
      }
    } catch (trackingError) {
      console.error("Failed to push begin_checkout event:", trackingError);
    }

    const productIds = selectedServices.map((service) => {
      const prod = service.product || (service.service && typeof service.service === 'object' ? service.service : null);
      return prod?.id;
    });

    const payload = {
      date: `${selectedDate?.split('T')[0]} ${selectedTime}`,
      start_before: `${selectedDate?.split('T')[0]} ${selectedTime}`,
      duration: totalDuration,
      lng: lngToUse,
      lat: latToUse,
      address_id: address.id,
      hotel_id: hotelId,
      zip_code: zip_code,
      ...therapistPayload,
      street: street,
      city,
      state: state,
      state_code: state_code,
      country: country,
      country_code: country_code,
      street_number: street_number,
      device_type: `${navigator.userAgentData?.platform} - ${navigator.userAgentData?.brands?.[1]?.brand}`,
      device_version: navigator.userAgentData?.brands?.[1]?.version,
      client_id: auth?.user?.id,
      status: 'CREATED',
      total_fare: selectedServices
        .map(service => {
          const prod = service.product || (service.service && typeof service.service === 'object' ? service.service : null);
          return prod?.price || 0;
        })
        .reduce((acc, price) => acc + price, 0),
      promocode: promotions?.id && alertMessage === t("promo.applied_successfully") ? promoCode : "",
      duo: duo,
  subtotal: finalSubtotal,
  products: productIds,
  product_id: (selectedServices[0] && (selectedServices[0].product?.id || (selectedServices[0].service && typeof selectedServices[0].service === 'object' ? selectedServices[0].service.id : null))) || null,
      message: message,
      phone: phoneNumber || auth?.user?.phone,
      no_preferences,
      locale: i18n.language,
    };

    console.log('Final payload:', payload);

    if (selectedCard) {
      payload.token = selectedCard.token;
    }

    if (finalSubtotal === 0) {
      payload.offline_payment = true;
    }
    try {
      const response = await BookingService.store(payload, api);
      console.log({ response });
      setEditGuardEnabled(false);
      if (finalSubtotal === 0) {
        payload.offline_payment = true;
        navigate('/commande');
      } else {
        const transactionData = response?.data?.transaction_data || {};
        const paymentLightboxScript = transactionData?.payment_lightbox;
        const paymentUrl = transactionData?.payment_url;
        if (!paymentLightboxScript || !paymentUrl) {
          console.error("Missing Wallee lightbox URLs for hotel booking payment flow.");
          setSubmitting(false);
          return;
        }
        const allowedPaymentMethods = extractAllowedPaymentMethods(transactionData);
        const dedupeKey = createEventDedupeKey(
          "add_payment_info",
          response?.data?.id || transactionData?.transaction_id || "hotel_booking_payment"
        );
        const trackingContext = {
          value: trackingValue,
          coupon: trackingCoupon,
          items: trackingItems,
          userData: trackingUserData,
          dedupeKey,
        };

        if (selectedCard) {
          pushAddPaymentInfoFromSelection({
            paymentType: resolvePaymentTypeFromSelectedCard(selectedCard),
            ...trackingContext,
          });
          loadLightbox(paymentLightboxScript, paymentUrl);
        } else if (allowedPaymentMethods.length > 0) {
          paymentStartedRef.current = true;
          setPaymentMethodModalState({
            open: true,
            methods: allowedPaymentMethods,
            context: {
              paymentLightboxScript,
              paymentUrl,
              trackingContext,
            },
          });
        } else {
          loadLightbox(paymentLightboxScript, paymentUrl);
        }
      }
    } catch (e) {
      console.error('Booking submission error:', e);
      const message = e?.response?.data?.message;
      if (e?.response?.status === 422 && message) {
        resetPromoToFullPrice(message);
      }
      setSubmitting(false);
    }
  };

  const addMinutesToTime = (time, duration) => {
    if (typeof time !== "string" || !time.includes(":")) return "";
    const [h, m] = time.split(":").map(n => parseInt(n, 10));
    if (isNaN(h) || isNaN(m)) return "";
    const date = new Date();
    date.setHours(h, m + duration);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit", minute: "2-digit"
    });
  };

  const formatSelectedDate = iso => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      month: "short", day: "numeric", year: "numeric"
    }).format(d);
  };

  const previousPathname = sessionStorage.getItem("previousPath");
  // sessionStorage.removeItem("previousPath");
  const timeSeparator = i18n.language === 'fr' ? 'à' : 'to';

  const handleComparisonConfirm = async () => {

    if (!isCguChecked) {
      showCguValidationError(comparisonCguSectionRef);
      return;
    }

    const sumPrices = booking =>
      (Array.isArray(booking.products) ? booking.products : [booking.product])
        .reduce((sum, p) => sum + (p?.price || 0), 0);

    const oldTotal = sumPrices(oldBooking);
    const newTotal = sumPrices(newBooking);
    const priceDiff = newTotal - oldTotal;

    const buildPayload = () => {
      const isSequentialBooking = sessionStorage.getItem("editingSequential") === "true";
      const duoFlag = !isSequentialBooking && newBooking?.duo ? 1 : 0;
      const datePart = newBooking?.date?.split('T')[0] || '';
      const bookingAddress = newBooking?.address || address || {};

      return {
        date: `${datePart} ${newBooking?.time || ''}`,
        start_before: `${datePart} ${newBooking?.time || ''}`,
        duration: totalDuration,
        status: 'UPDATED',

        hotel_id: hotelId,
        address_id: bookingAddress.id,
        zip_code: bookingAddress.zip_code,
        street: bookingAddress.street,
        street_number: bookingAddress.street_number,
        city: bookingAddress.city,
        state: bookingAddress.state,
        state_code: bookingAddress.state_code,
        country: bookingAddress.country,
        country_code: bookingAddress.country_code,
        longitude: bookingAddress.longitude,
        latitude: bookingAddress.latitude,

        total_fare: newTotal,
        subtotal: newTotal,

        // Fix: Use products array instead of product
        product_id: duoFlag ? null : newBooking?.products?.[0]?.id,
        products: duoFlag
          ? (newBooking?.products ? newBooking.products.map(p => p.id) : [])
          : [newBooking?.products?.[0]?.id],

        // Fix: Handle sequential vs DUO therapist assignment
        therapist_id: isSequentialBooking
          ? [newBooking?.therapists?.[0]?.id] // Only 1 therapist for sequential
          : duoFlag
            ? (newBooking?.therapists ? newBooking.therapists.map(t => t.id) : [])
            : [newBooking?.therapists?.[0]?.id],

        // skip_update_email: true,
        promocode: newBooking?.promocode || '',
        message: newBooking?.message || '',
        phone: newBooking?.phone || phoneNumber,
        duo: duoFlag,
        no_preferences: newBooking?.no_preferences,
        locale: i18n.language,
      };
    };


    if (priceDiff > 0) {
      try {
        const updatePayload = buildPayload();
        const payResp = await BookingService.payDifference(
          { booking_id: oldBooking.id, old_total: oldTotal, new_total: newTotal, duration: totalDuration,
            success_update: updatePayload,
           },
          api
        );

        const lightboxUrl = payResp.transaction_data.data.payment_lightbox;
        const paymentUrl = payResp.transaction_data.data.payment_url;
        if (!lightboxUrl || !paymentUrl) {
          notify.warn(t('payment:error_processing'));
          return;
        }
        const allowedPaymentMethods = extractAllowedPaymentMethods(payResp?.transaction_data);
        const trackingItems = buildItemsFromSelectedServices(selectedServices, {
          address,
          hotelId,
        });
        const trackingValue =
          Number.isFinite(Number(priceDiff))
            ? Number(priceDiff)
            : calculateValueFromItems(trackingItems);
        const resolvedTrackingUser = await resolveTrackingUser();
        const trackingContext = {
          value: trackingValue,
          coupon: newBooking?.promocode || undefined,
          items: trackingItems,
          userData: buildUserDataObject({
            user: resolvedTrackingUser,
            booking: newBooking,
            address: newBooking?.address || address,
            phoneNumber: newBooking?.phone || phoneNumber || resolvedTrackingUser?.phone || auth?.user?.phone,
            emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
            isConnectedUser: Boolean(auth?.user?.id),
          }),
          dedupeKey: createEventDedupeKey("add_payment_info", oldBooking?.id || "hotel_extra_payment"),
        };

        if (allowedPaymentMethods.length > 0) {
          paymentStartedRef.current = true;
          setPaymentMethodModalState({
            open: true,
            methods: allowedPaymentMethods,
            context: {
              paymentLightboxScript: lightboxUrl,
              paymentUrl,
              trackingContext,
            },
          });
        } else {
          loadLightbox(lightboxUrl, paymentUrl);
        }
      }
      catch (err) {
        console.error(err);
        notify.warn(t('payment:error_processing'));
      }

      return;
    }

    if (priceDiff < 0) {
      await BookingService.refundDifference(
        { old_total: oldTotal, new_total: newTotal },
        oldBooking.id,
        api
      );
      await BookingService.update(oldBooking.id, buildPayload(), api);
    }

    if (priceDiff === 0) {
      loader.start();
      await BookingService.update(oldBooking.id, buildPayload(), api);
      setEditGuardEnabled(false);
    }
    sessionStorage.clear();


    loader.stop();
    setTimeout(() => navigate('/commande'), 0);

  };

  let currentTime = selectedTime;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  const deleteCard = async (token) => {
    try {
      await PaymentService.deleteCard(token, api);
      fetchCards();
    } catch (err) {
      console.error(err);
    }
  }

  const fetchCards = async () => {
    try {
      const response = await AuthService.me({ id: auth.user.id, with: 'cards.brand,primary_address' }, api);
      setCards(response.user.cards);
      setTrackingUser(response?.user || auth?.user || null);
      if (response?.user?.cards?.length > 0) {
        setSelectedCard(response.user.cards[0])
      } else {
        setSelectedCard(null);
      }
    } catch (err) {
      console.error(err);
    }
  }

  const handleCardChange = ($event) => {
    if ($event.currentTarget.dataset.id) {
      const card = cards.find((card) => card.id === Number($event.currentTarget.dataset.id));
      if (card) {
        setSelectedCard(card)
      }
    }
    else setSelectedCard(null);
  }

  useEffect(() => {
    fetchCards();
  }, []);

  const handleLoginClick = () => {
    // state before navigation to login
    const paymentState = {
      address,
      hotel,
      hotelId,
      hotel_id: hotelId,
      duo,
      phone,
      totalDuration,
      totalPrice,
      selectedServices,
      selectedTherapist,
      selectedDate,
      selectedTime
    };
    const prev = JSON.parse(sessionStorage.getItem('newBooking') || '{}');
    sessionStorage.setItem('state', JSON.stringify({ ...prev, ...paymentState }));
    sessionStorage.setItem('redirectAfterLogin', '/hotel-payment');
    navigate('/login', {
      state: { from: location.pathname }
    });
  };

  const handleRegisterClick = () => {
    // state before navigation to register
    const paymentState = {
      address,
      hotel,
      hotelId,
      hotel_id: hotelId,
      duo,
      phone,
      totalDuration,
      totalPrice,
      selectedServices,
      selectedTherapist,
      selectedDate,
      selectedTime
    };
    const prev = JSON.parse(sessionStorage.getItem('newBooking') || '{}');
    sessionStorage.setItem('state', JSON.stringify({ ...prev, ...paymentState }));
    sessionStorage.setItem('redirectAfterLogin', '/hotel-payment');
    navigate('/register');
  };

  const openGuestCheckout = useCallback(() => {
    if (!auth?.user) {
      setGuestCheckoutOpen(true);
    }
  }, [auth?.user]);

  const closeGuestCheckout = useCallback(() => {
    setGuestCheckoutOpen(false);
  }, []);

  const urlsFly = {
    cgu: i18n.language === 'fr' 
      ? 'https://flyspa.ch/conditions-generales-utilisation/' 
      : 'https://flyspa.ch/en/general-terms-of-use/',
    privacy: i18n.language === 'fr' 
      ? 'https://flyspa.ch/politique-de-confidentialite/' 
      : 'https://flyspa.ch/en/privacy-policy/'
  };
  const clientFullName = getUserDisplayName(auth?.user);

  return (
    <>


      <MenuHeader />
      <NavHotel page="payment" />
      <div className="content-wrapper payment-page">
        {!showComparison && (
          <div className="content-wrapper duo-pay payment-page__summaries margin-top-min-30">

            {selectedServices && selectedServices.length > 0 && Array.isArray(selectedTherapist) && selectedTherapist.length > 0 && (
              isSingleTherapistSequential ? (
                // ONE CARD for single therapist + multiple products (sequential)
                (() => {
                  const serviceTherapist = selectedTherapist[0];
                  let cursorTime = selectedTime;

                  return (
                    <div className="column">
                      <div className="summary-box">
                        {/* Therapist header */}
                        {serviceTherapist && (
                          <>
                          <div className="name">
                            <div
                              className="image"
                              style={{
                                backgroundImage: `url(${serviceTherapist.media?.length ? serviceTherapist.media[0].original_url : therapeut1})`,
                              }}
                            />
                            <div className="title">
                              <div className="text-wrapper-2">{serviceTherapist.firstname}</div>
                              <div className="rating">
                                <div className="stars">
                                  <div className="text-wrapper-3">
                                    <img className="https-app" src={star2} alt="Rating" />
                                  </div>
                                </div>
                                <div className="text-wrapper-4">{serviceTherapist?.extra?.rating}/5</div>
                              </div>
                            </div>
                          </div>
                          <div className="text-wrapper-6" style={{paddingLeft: 0}}>{t('task_resume')}</div>
                          <p className="text-wrapper-5">Date: {formatSelectedDate(selectedDate)}</p>
                          </>
                        )}

                        {/* Render ALL inside ONE card */}
                        {translatedSelectedServices.map((service, idx) => {
                          // prefer the product object for numeric fields
                          const prod = service.product || (service.service && typeof service.service === 'object' ? service.service : null);
                          const start = cursorTime;
                          const end = addMinutesToTime(cursorTime, Number(prod?.duration || 0));
                          cursorTime = end;

                          return (
                            <React.Fragment key={prod?.id ?? idx}>
                              <div className="div-2" style={{borderBottom: 0, padding: 0}}>
                                <div className="text-wrapper-6" style={{paddingLeft: 0}}>{idx == 0 ? t('me') : t('guest') + idx} - {prod?.category?.title} ({prod?.duration}min)</div>
                              </div>
                              <div className="div-2">
                                <p className="text-wrapper-5">
                                  {t('duree')}: {prod?.duration}min - {start} {timeSeparator} {end}
                                </p>
                                <div className="text-wrapper-6" style={{paddingLeft: 0}}>{formatChf(service.product.price)} CHF</div>
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {/* Address + phone */}
                        <div className="div-2">
                          <p className="text-wrapper-5">
                            {clientFullName}
                            {clientFullName && (<br/>)}
                            {address?.street} {address?.street_number}
                            <br />
                            {address?.zip_code} {address?.city}
                            <br />
                            {!showInput && (phoneNumber)}
                            {showInput && (
                              <div className="edit-phone-payload">
                                <PhoneInput
                                  type="tel"
                                  name="phone"
                                  value={phoneNumber}
                                  defaultCountry="ch"
                                  forceDialCode="true"
                                  disableCountryGuess="true"
                                  onChange={(phoneNumber) => handleTelChange(phoneNumber)}
                                  placeholder={t('enter_new_phone_number')}
                                  className="phone-input"
                                  inputMode="tel"
                                  maxLength={15}
                                />
                                <button onClick={handleSave} className="btn save-button">{t('save')}</button>
                              </div>
                            )}
                            {alertMessageTel && <div className="alert">{alertMessageTel}</div>}
                          </p>
                        </div>

                        {/* Single message textarea */}
                        <div className="message-au-flypro">
                          <div className="title-2">
                            <div className="text-wrapper-8">
                              {t('message_to_flypro')}{" "}
                              <span className="tooltip-wrapper">
                                <img className="https-app tooltip-trigger" src={questionmark} alt="Info" />
                                <div className="tooltip-box">{t('message_mark')}</div>
                              </span>
                            </div>
                          </div>
                          <textarea
                            placeholder={t('example_message')}
                            className="form-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (translatedSelectedServices && translatedSelectedServices.length > 0 && Array.isArray(selectedTherapist) && selectedTherapist.length > 0 &&
                translatedSelectedServices.map((displayService, serviceIndex) => {
                  // raw service (may contain original objects) for ids if needed
                  const rawService = selectedServices[serviceIndex] || {};
                  const serviceTherapist = selectedTherapist[serviceIndex % selectedTherapist.length];
                  const startTime = currentTime;
                  console.log('startTime', startTime)
                  const duration = Number(displayService?.product?.duration || rawService?.product?.duration || 0);
                  const endTime = addMinutesToTime(startTime, duration);
                  currentTime = endTime;
                  console.log('startTime', startTime)
                  console.log('endTime', endTime)

                  return (
                    <div className="column" key={rawService.id ?? displayService.product?.id ?? serviceIndex}>
                      <div className="summary-box">
                        {serviceTherapist && (
                          <>
                          <div className="name">
                            <div
                              className="image"
                              style={{
                                backgroundImage: `url(${serviceTherapist.media?.length ? serviceTherapist.media[0].original_url : therapeut1})`,
                              }}
                            ></div>
                            <div className="title">
                              <div className="text-wrapper-2">
                                {serviceTherapist.firstname}
                              </div>
                              <div className="rating">
                                <div className="stars">
                                  <div className="text-wrapper-3">
                                    <img className="https-app" src={star2} alt="Rating" />
                                  </div>
                                </div>
                                <div className="text-wrapper-4">{serviceTherapist?.extra?.rating}/5</div>
                              </div>
                            </div>
                          </div>
                          <div className="text-wrapper-6" style={{paddingLeft: 0}}>{t('task_resume')}</div>
                          <p className="text-wrapper-5">Date: {formatSelectedDate(selectedDate)}</p>
                          </>
                        )}

                        <div className="div-2" style={{borderBottom: 0, padding: 0}}>
                          {(() => {
                            const prod = displayService.product || rawService.product || (displayService.service && typeof displayService.service === 'object' ? displayService.service : (rawService.service && typeof rawService.service === 'object' ? rawService.service : null));
                            return <div className="text-wrapper-6" style={{paddingLeft: 0}}>{serviceIndex == 0 ? t('me') : t('guest') + serviceIndex} - {prod?.category?.title} ({prod?.duration}min)</div>;
                          })()}
                        </div>

                        <div className="div-2">
                          {selectedServices.length <= 1 || duo ? (
                            <p className="text-wrapper-5">
                              {(() => {
                                const prod =
                                  displayService.product ||
                                  rawService.product ||
                                  (typeof displayService.service === 'object'
                                    ? displayService.service
                                    : typeof rawService.service === 'object'
                                    ? rawService.service
                                    : null);

                                return (
                                  <>
                                    {t('duree')}: {prod?.duration}min - {selectedTime} {timeSeparator}{" "}
                                  </>
                                );
                              })()}
                              {(() => {
                                const prod = displayService.product || rawService.product || (displayService.service && typeof displayService.service === 'object' ? displayService.service : (rawService.service && typeof rawService.service === 'object' ? rawService.service : null));
                                return addMinutesToTime(selectedTime, Number(prod?.duration || 0));
                              })()}
                            </p>
                          ) : (
                            <p className="text-wrapper-5">
                              {(() => {
                                const prod =
                                  displayService.product ||
                                  rawService.product ||
                                  (typeof displayService.service === 'object'
                                    ? displayService.service
                                    : typeof rawService.service === 'object'
                                    ? rawService.service
                                    : null);

                                return (
                                  <>
                                    {t('duree')}: {prod?.duration}min – {startTime} {timeSeparator} {endTime}
                                  </>
                                );
                              })()}
                            </p>
                          )}
                          <div className="text-wrapper-6">{formatChf(displayService.product?.price ?? rawService.product?.price)} CHF</div>
                        </div>

                        <div className="div-2">
                          <p className="text-wrapper-5">
                            {clientFullName}
                            {clientFullName && (<br/>)}
                            {address?.street} {address?.street_number}
                            <br />
                            {address?.zip_code} {address?.city}
                            <br />
                            {!showInput && (
                              phoneNumber
                            )}
                            {showInput && (
                              <div className="edit-phone-payload">
                                <PhoneInput
                                  type="tel"
                                  name="phone"
                                  value={phoneNumber}
                                  defaultCountry="ch"
                                  forceDialCode="true"
                                  disableCountryGuess="true"
                                  onChange={(phoneNumber) => handleTelChange(phoneNumber)}
                                  placeholder={t('enter_new_phone_number')}
                                  className="phone-input"
                                  inputMode="tel"
                                  maxLength={15}
                                />
                                <button
                                  onClick={handleSave}
                                  className="btn save-button">{t('save')}</button>
                              </div>
                            )}
                            {alertMessageTel && <div className="alert">{alertMessageTel}</div>}
                          </p>
                        </div>
                        <div className="message-au-flypro">
                          <div className="title-2">
                            <div className="text-wrapper-8">
                              {t('message_to_flypro')}{" "}
                              <span className="tooltip-wrapper">
                                <img className="https-app tooltip-trigger" src={questionmark} alt="Info" />
                                <div className="tooltip-box">
                                  {t('message_mark')}
                                </div>
                              </span>
                            </div>
                          </div>
                          <textarea

                            placeholder={t('example_message')}
                            className="form-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                          />

                        </div>
                      </div>
                    </div>
                  );
                })
              )
            )}
          </div>
        )}


        <div className={`column-2 ${showComparison ? 'full' : ''} margin-top-min-30`}>
          {showComparison && (
            <div className="cmp-wrapper">
        <ComparisonCard
          oldBooking={translatedOldBooking}
          newBooking={translatedNewBooking}
                isCguChecked={isCguChecked}
                onCguToggle={setIsCguChecked}
                footer={(
                  <>

                    <div className="total">
                      <div className="price">
                        <div className="list-item-2 list-item-3">
                          <span>{t('total_incl_vat')}</span>
                          <div className="text-wrapper-6">{formatChf(totalPrice)} CHF</div>
                        </div>
                        <div className="form-4 form-with-button">
                          <input
                            type="text"
                            placeholder={t('enter_promo_code')}
                            className="form-5 max-width-50"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            onClick={() => {
                              if (!auth?.user) {
                                setAlertMessage("Login for promotions");
                              }
                            }}
                          />
                          <button className="form-3"
                            disabled={!auth?.user}
                            style={{
                              opacity: auth?.user ? 1 : 0.5,
                              cursor: auth?.user ? 'pointer' : 'not-allowed'
                            }}
                            onClick={handlePromoCodeValidation}>
                            {t('validate')}
                          </button>
                        </div>
                        {alertMessage && <div className="alert">{alertMessage}</div>}
                      </div>

                      <div
                        ref={comparisonCguSectionRef}
                        className={`checkbox-2${showCguError ? ' checkbox-2--error' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="checkbox"
                          id="cgu"
                          checked={isCguChecked}
                          onChange={(e) => {
                            setIsCguChecked(e.target.checked);
                            if (e.target.checked) {
                              setShowCguError(false);
                              setSubmitting(false);
                            }
                          }}
                          aria-invalid={showCguError}
                        />
                        <label htmlFor="cgu" className="accepter-les-CGU-et">
                          <span>{t('payment:accept_terms')} </span>
                          <a href={urlsFly.cgu} style={{ color: '#9aa3ad', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">conditions générales</a>
                          <span> et la </span>
                          <a href={urlsFly.privacy} style={{ color: '#9aa3ad', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">politique de confidentialité</a>
                          <span> *</span>
                        </label>
                      </div>

                    </div>
                  </>
                )}
                onConfirm={handleComparisonConfirm}
                onCancel={() => {
                  setShowCancelConfirm(true);
                  setEditGuardEnabled(false);
                }}
              />
            </div>
          )}

          <Modal
            isOpen={availabilityConflictOpen}
            onRequestClose={handleAvailabilityConflictConfirm}
            shouldCloseOnOverlayClick={false}
            style={{
              content: {
                width: "60%",
                maxWidth: "420px",
                maxHeight: "260px",
                margin: "auto",
                padding: "30px",
                borderRadius: "12px",
                textAlign: "center"
              }
            }}
          >
            <h2>{getTranslation('payment:therapist_unavailable_title', 'flypro unavailable')}</h2>
            <p>{getTranslation('payment:therapist_unavailable', 'The flypro you selected is no longer available for this time slot. Please choose another flypro.')}</p>

            <div style={{ display: "flex", gap: "14px", justifyContent: "center", marginTop: "28px" }}>
              <button
                onClick={handleAvailabilityConflictConfirm}
                style={{
                  padding: "12px 28px",
                  border: "none",
                  borderRadius: "8px",
                  background: "#393d44",
                  color: "#faef9b",
                  fontFamily: "MarcellusRegular",
                  fontSize: "17px",
                  cursor: "pointer"
                }}
              >
                {getTranslation('payment:choose_therapist', 'Choose therapist')}
              </button>
            </div>
          </Modal>

          <Modal
            isOpen={showCancelConfirm}
            onRequestClose={closeCancelModal}
            style={{
              content: {
                width: "60%", maxWidth: "420px", maxHeight: "250px",
                margin: "auto", padding: "30px", borderRadius: "12px", textAlign: "center"
              }
            }}
          >
            <h2>Annuler la modification ?</h2>
            <p>Vos changements en cours seront perdus.<br /><br />Voulez‑vous vraiment quitter ?</p>

            <div style={{ display: "flex", gap: "14px", justifyContent: "center", marginTop: "28px" }}>

              <button
                onClick={closeCancelModal}
                style={{
                  padding: "12px 28px",
                  border: "1.5px solid #393d44",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#393d44",
                  fontFamily: "MarcellusRegular",
                  fontSize: "17px",
                  cursor: "pointer"
                }}
              >
                Retour
              </button>

              <button
                onClick={() => {
                  sessionStorage.clear();
                  navigate('/commande?id=' + (oldBooking?.id ?? ''));
                }}
                style={{
                  padding: "12px 28px",
                  border: "none",
                  borderRadius: "8px",
                  background: "#393d44",
                  color: "#faef9b",
                  fontFamily: "MarcellusRegular",
                  fontSize: "17px",
                  cursor: "pointer"
                }}
              >
                Oui, annuler
              </button>
            </div>
          </Modal>



          {!showComparison && (
            <>
              <div className="total">
                <div className="price">
                  <div className="list-item-2 list-item-3">
                    <span>{t('total_incl_vat')}</span>
                    <div className="text-wrapper-6">{formatChf(totalPrice)} CHF</div>
                  </div>
                  <div className="form-4 form-with-button">
                    <input
                      type="text"
                      placeholder={t('enter_promo_code')}
                      className="form-5"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                    />
                    <button className="form-3" onClick={handlePromoCodeValidation}>
                      {t('validate')}
                    </button>
                  </div>
                  {alertMessage && <div className="alert">{alertMessage}</div>}
                </div>
                {!showComparison && (
                  <div
                    ref={cguSectionRef}
                    className={`checkbox-2${showCguError ? ' checkbox-2--error' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="checkbox"
                      id="cgu"
                      checked={isCguChecked}
                      onChange={(e) => {
                        setIsCguChecked(e.target.checked);
                        if (e.target.checked) {
                          setShowCguError(false);
                          setSubmitting(false);
                        }
                      }}
                      aria-invalid={showCguError}
                    />
                    <label htmlFor="cgu" className="accepter-les-CGU-et">
                      <span>{t('payment:accept_terms')} </span>
                      <a href={urlsFly.cgu} style={{ color: '#000', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">{t('payment:terms_and_conditions')}</a>
                      <span> {t('payment:est')} </span>
                      <a href={urlsFly.privacy} style={{ color: '#000', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">{t('payment:privacy_policy')}</a>
                      <span> *</span>
                    </label>
                  </div>
                )}
              </div>
              {cards.length > 0 && discountedPrice?.toFixed(2) > 0 && (
                <div className="payment-card-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {
                    cards?.map((card, index) => (
                      <div key={index} className="payment-card flex-row align-center" style={{ gap: 20 }}>
                        <div className="flex-row gap-10 align-center" style={{ flex: 1 }}>
                          <img className='brand-icon' src={card.brand.logo} alt='card-brand' width={45} />
                          <div className='flex-column'>
                            <span className='card-holder'>{card.card_holder}</span>
                            <span className='hashed'>{card.hashed.replaceAll('X', '•')}</span>
                            <span className='expiry'>Expire {new Date(card.expiry).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}</span>
                          </div>
                          <IconButton onClick={() => deleteCard(card.token)}><img src='assets/images/icon-trash.svg' alt='delete' /></IconButton>
                        </div>
                        <input type='radio' name='card' style={{ cursor: 'pointer' }} checked={selectedCard === card} onChange={handleCardChange} data-id={card.id}></input>
                      </div>
                    ))
                  }
                  <div className="payment-card flex-row space-between align-center">
                    <span className='card-holder'>{t('newcard')}</span>
                    <input type='radio' name='card' style={{ cursor: 'pointer' }} checked={!selectedCard} onChange={handleCardChange}></input>
                  </div>
                </div>
              )}

              <button
                onClick={auth?.user ? handleSubmit : openGuestCheckout}
                className="button-validation"
                type="button"
                disabled={submitting}
                style={{
                  opacity: submitting ? 0.5 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="button-validation__content">
                  <span className="button-validation__label">{t('pay')}</span>
                  <span className="button-validation__amount">{formatChf(discountedPrice)} CHF{submitting ? " ..." : ""}</span>
                </span>
              </button>
            </>
          )}
        </div>
      </div>
      <GuestCheckoutDialog
        open={guestCheckoutOpen}
        onClose={closeGuestCheckout}
        onRegister={handleRegisterClick}
        onLogin={handleLoginClick}
        title={t('payment:guest_checkout_title')}
        message={t('payment:guest_checkout_message')}
        registerLabel={t('payment:register')}
        loginLabel={t('payment:login')}
      />
      <GtmPaymentMethodModal
        open={paymentMethodModalState.open}
        methods={paymentMethodModalState.methods}
        onSelect={handlePaymentMethodSelection}
        onCancel={closePaymentMethodModal}
      />
    </>
  );
}

function Payment() {
  return (
    <>
      <PaymentForm totalPriceCHF={120} />
    </>

  );
}

export default Payment;
